import { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Clock, Tag, Star, ChevronRight, Facebook, Linkedin, Mail, Link as LinkIcon, Check } from "lucide-react";
import DOMPurify from "dompurify";
import { SocialLinks } from "./LandingPage";

const API_BASE = window.location.origin;

// Strict whitelist for blog HTML content (LLM-generated or admin-edited).
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: ["h2", "h3", "h4", "p", "strong", "em", "b", "i", "u", "ul", "ol", "li", "blockquote", "a", "br", "hr", "code", "pre"],
  ALLOWED_ATTR: ["href", "title", "target", "rel"],
  ALLOW_DATA_ATTR: false,
};

function BlogListPage() {
  const [articles, setArticles] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/blog/articles?page=${page}&limit=12`);
      const data = await res.json();
      setArticles(data.articles || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch (err) { console.warn("Blog list load failed:", err?.message); }
    setLoading(false);
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const featured = articles.filter(a => a.featured);
  const regular = articles.filter(a => !a.featured);

  return (
    <div className="min-h-screen bg-[#FBF9F5]">
      <Helmet>
        <title>Blog — MonBeauSite | Conseils pour votre présence en ligne</title>
        <meta name="description" content="Conseils, astuces et stratégies pour développer votre présence en ligne. Articles SEO pour commerçants, artisans et restaurateurs." />
      </Helmet>

      {/* Header */}
      <header className="border-b border-[#BFBFBF] bg-white">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/api/static/images/web/logo_mbs.png" alt="MonBeauSite" className="h-8 w-8 object-contain rounded-lg ring-1 ring-[#D4AF37]/25 shadow-sm bg-white" />
            <span className="text-lg font-semibold text-[#2F4F4F] tracking-tight font-montserrat">MonBeauSite</span>
          </Link>
          <nav className="flex items-center gap-6 text-sm">
            <Link to="/" className="text-[#6B6B6B] hover:text-[#2F4F4F] transition-colors">Accueil</Link>
            <span className="text-[#2F4F4F] font-medium">Blog</span>
          </nav>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <p className="section-num font-mono-tech text-xs tracking-widest text-[#C46A4A] mb-4 inline-flex">BLOG // RESSOURCES</p>
          <h1 className="text-3xl sm:text-4xl font-medium text-[#2F4F4F] mb-3 font-montserrat tracking-tight">
            Le Blog <span className="font-playfair italic text-[#C46A4A] font-normal">MonBeauSite</span>
          </h1>
          <p className="text-[#6B6B6B] text-base max-w-xl mx-auto">
            Conseils concrets pour développer votre activité grâce au web.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-20 text-[#6B6B6B]">Chargement...</div>
        ) : articles.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-[#6B6B6B]">Les articles arrivent bientôt.</p>
          </div>
        ) : (
          <>
            {/* Featured articles */}
            {featured.length > 0 && (
              <div className="mb-12">
                <div className="flex items-center gap-2 mb-6">
                  <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                  <span className="text-xs text-[#2F4F4F] font-semibold uppercase tracking-wider">Articles essentiels</span>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  {featured.map(a => <ArticleCard key={a.id} article={a} featured />)}
                </div>
              </div>
            )}

            {/* Regular articles */}
            {regular.length > 0 && (
              <div>
                {featured.length > 0 && (
                  <h2 className="text-xs text-[#6B6B6B] font-semibold uppercase tracking-wider mb-6">Derniers articles</h2>
                )}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {regular.map(a => <ArticleCard key={a.id} article={a} />)}
                </div>
              </div>
            )}

            {/* Pagination */}
            {pages > 1 && (
              <div className="flex justify-center gap-3 mt-12">
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                  className="px-4 py-2 text-sm rounded-full border border-[#BFBFBF] text-[#6B6B6B] hover:border-[#2F4F4F] disabled:opacity-40">
                  Précédent
                </button>
                <span className="px-4 py-2 text-sm text-[#6B6B6B]">{page} / {pages}</span>
                <button disabled={page >= pages} onClick={() => setPage(p => p + 1)}
                  className="px-4 py-2 text-sm rounded-full border border-[#BFBFBF] text-[#6B6B6B] hover:border-[#2F4F4F] disabled:opacity-40">
                  Suivant
                </button>
              </div>
            )}
          </>
        )}
      </main>

      {/* CTA */}
      <section className="bg-[#1F3535] py-16 mt-12 relative overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" aria-hidden="true" />
        <div className="max-w-3xl mx-auto px-6 text-center relative">
          <h2 className="text-2xl text-white font-medium mb-3 font-montserrat tracking-tight">
            Prêt à <span className="font-playfair italic text-[#E8D8A9] font-normal">donner vie</span> à votre projet ?
          </h2>
          <p className="text-[#E8D8A9]/80 mb-6 text-sm">Découvrez gratuitement ce que MonBeauSite peut créer pour vous.</p>
          <Link to="/#services" className="inline-block px-8 py-3 bg-[#C46A4A] text-white rounded-full font-semibold text-sm hover:bg-[#A6553A] transition-colors shadow-lg shadow-[#C46A4A]/20">
            Demander ma démo gratuite
          </Link>
        </div>
      </section>

      <footer className="bg-[#1F3535] border-t border-[#D4AF37]/15 py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col items-center gap-4">
          <SocialLinks variant="dark" size="sm" />
          <p className="text-white/40 text-xs">MonBeauSite — Émilien TERROT | Tous droits réservés</p>
        </div>
      </footer>
    </div>
  );
}


function ArticleCard({ article, featured }) {
  const date = article.published_at ? new Date(article.published_at).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" }) : "";

  return (
    <Link to={`/blog/${article.slug}`} data-testid={`blog-card-${article.slug}`}
      className={`group bg-white border rounded-xl overflow-hidden hover:shadow-md transition-all ${featured ? 'border-[#2F4F4F]/20' : 'border-[#BFBFBF]'}`}>
      {article.cover_image && (
        <div className={`w-full overflow-hidden bg-[#FBF9F5] ${featured ? 'aspect-[16/9]' : 'aspect-[16/10]'}`}>
          <img
            src={article.cover_image}
            alt={article.title}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
      )}
      <div className="p-6">
        {featured && (
          <div className="flex items-center gap-1 mb-3">
            <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
            <span className="text-[10px] text-amber-600 font-semibold uppercase tracking-wider">Essentiel</span>
          </div>
        )}
        <h3 className="text-[#2F4F4F] font-medium text-base mb-2 group-hover:text-[#3F6B6B] transition-colors leading-snug font-montserrat tracking-tight">
          {article.title}
        </h3>
        {article.summary && (
          <p className="text-[#6B6B6B] text-xs leading-relaxed mb-3 line-clamp-2">{article.summary}</p>
        )}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {date && <span className="text-[10px] text-[#999] flex items-center gap-1"><Clock className="w-3 h-3" />{date}</span>}
            {article.keywords?.length > 0 && (
              <span className="text-[10px] text-[#2F4F4F]/60 flex items-center gap-1"><Tag className="w-3 h-3" />{article.keywords[0]}</span>
            )}
          </div>
          <ChevronRight className="w-4 h-4 text-[#2F4F4F]/30 group-hover:text-[#2F4F4F] transition-colors" />
        </div>
      </div>
    </Link>
  );
}


function BlogArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/blog/articles/${slug}`);
        if (!res.ok) throw new Error("Not found");
        setArticle(await res.json());
      } catch {
        setArticle(null);
      }
      setLoading(false);
    })();
  }, [slug]);

  if (loading) return <div className="min-h-screen bg-[#FBF9F5] flex items-center justify-center text-[#6B6B6B]">Chargement...</div>;
  if (!article) return (
    <div className="min-h-screen bg-[#FBF9F5] flex items-center justify-center">
      <div className="text-center">
        <p className="text-[#6B6B6B] mb-4">Article non trouvé</p>
        <button onClick={() => navigate("/blog")} className="text-[#2F4F4F] underline text-sm">Retour au blog</button>
      </div>
    </div>
  );

  const date = article.published_at ? new Date(article.published_at).toLocaleDateString("fr-FR", { year: "numeric", month: "long", day: "numeric" }) : "";

  return (
    <div className="min-h-screen bg-[#FBF9F5]">
      <Helmet>
        <title>{`${article.meta_title || article.title} — MonBeauSite Blog`}</title>
        <meta name="description" content={article.meta_description || article.summary || ""} />
        <meta name="keywords" content={(article.keywords || []).join(", ")} />
        <link rel="canonical" href={`https://monbeausite.com/blog/${article.slug}`} />
        <meta property="og:title" content={article.meta_title || article.title} />
        <meta property="og:description" content={article.meta_description || article.summary || ""} />
        <meta property="og:type" content="article" />
        {article.cover_image && <meta property="og:image" content={article.cover_image.startsWith("http") ? article.cover_image : `https://monbeausite.com${article.cover_image}`} />}
        {article.cover_image && <meta name="twitter:card" content="summary_large_image" />}
        {article.cover_image && <meta name="twitter:image" content={article.cover_image.startsWith("http") ? article.cover_image : `https://monbeausite.com${article.cover_image}`} />}
      </Helmet>

      <header className="border-b border-[#BFBFBF] bg-white">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center">
          <button onClick={() => navigate("/blog")} className="flex items-center gap-2 text-sm text-[#6B6B6B] hover:text-[#2F4F4F] transition-colors">
            <ArrowLeft className="w-4 h-4" /> Retour au blog
          </button>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 py-12">
        <header className="mb-10">
          {article.keywords?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {article.keywords.slice(0, 4).map(k => (
                <span key={k} className="text-[10px] bg-[#2F4F4F]/8 text-[#2F4F4F] px-2.5 py-0.5 rounded-full">{k}</span>
              ))}
            </div>
          )}
          <h1 className="text-2xl sm:text-3xl font-medium text-[#2F4F4F] leading-tight mb-4 font-montserrat tracking-tight">
            {article.title}
          </h1>
          <div className="flex items-center gap-4 text-xs text-[#999]">
            {date && <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{date}</span>}
            <span>Par Émilien TERROT</span>
          </div>
        </header>

        {article.cover_image && (
          <div className="aspect-[16/9] w-full overflow-hidden rounded-2xl bg-[#FBF9F5] mb-10">
            <img src={article.cover_image} alt={article.title} className="w-full h-full object-cover" />
          </div>
        )}

        <div className="prose prose-sm max-w-none
          [&_h2]:text-[#2F4F4F] [&_h2]:font-semibold [&_h2]:text-xl [&_h2]:mt-8 [&_h2]:mb-3 [&_h2]:font-[Playfair_Display,Georgia,serif]
          [&_h3]:text-[#2F4F4F] [&_h3]:font-medium [&_h3]:text-base [&_h3]:mt-6 [&_h3]:mb-2
          [&_p]:text-[#444] [&_p]:text-sm [&_p]:leading-relaxed [&_p]:mb-4
          [&_strong]:text-[#2F4F4F]
          [&_a]:text-[#2F4F4F] [&_a]:underline
          [&_ul]:text-sm [&_ul]:text-[#444] [&_ul]:space-y-1 [&_ul]:mb-4 [&_ul]:pl-5
          [&_li]:leading-relaxed
          [&_blockquote]:border-l-2 [&_blockquote]:border-[#2F4F4F] [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-[#666]
        " dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content || "", SANITIZE_CONFIG) }} />

        <ShareButtons title={article.title} slug={article.slug} />

        {/* CTA */}
        <div className="mt-12 bg-[#2F4F4F] rounded-xl p-8 text-center">
          <h2 className="text-xl text-white font-medium mb-2 font-montserrat tracking-tight">
            Prêt à <span className="font-playfair italic text-[#E8D8A9] font-normal">passer à l'action</span> ?
          </h2>
          <p className="text-white/70 text-sm mb-5">Découvrez gratuitement ce que MonBeauSite peut imaginer pour votre activité.</p>
          <Link to="/#services" className="inline-block px-8 py-3 bg-white text-[#2F4F4F] rounded-full font-semibold text-sm hover:bg-[#FBF9F5] transition-colors">
            Demander ma démo gratuite
          </Link>
        </div>
      </article>

      <footer className="bg-[#2F4F4F] py-8 mt-12">
        <div className="max-w-5xl mx-auto px-6 flex flex-col items-center gap-4">
          <SocialLinks variant="dark" size="sm" />
          <p className="text-white/40 text-xs">MonBeauSite — Émilien TERROT | Tous droits réservés</p>
        </div>
      </footer>
    </div>
  );
}

export { BlogListPage, BlogArticlePage };

function ShareButtons({ title, slug }) {
  const [copied, setCopied] = useState(false);
  const url = `https://monbeausite.com/blog/${slug}`;
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const links = [
    {
      name: "LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      Icon: Linkedin,
      testId: "share-linkedin",
    },
    {
      name: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      Icon: Facebook,
      testId: "share-facebook",
    },
    {
      name: "Email",
      href: `mailto:?subject=${encodedTitle}&body=${encodeURIComponent(`Je pense que cet article peut t'intéresser : ${url}`)}`,
      Icon: Mail,
      testId: "share-email",
    },
  ];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* silent fail */
    }
  };

  return (
    <div className="mt-10 pt-8 border-t border-[#D4AF37]/20" data-testid="blog-share-buttons">
      <p className="text-[10px] text-[#6B6B6B] uppercase tracking-[0.18em] font-semibold mb-4 text-center font-body">
        Partagez cet article
      </p>
      <div className="flex items-center justify-center gap-3 flex-wrap">
        {links.map(({ name, href, Icon, testId }) => (
          <a
            key={name}
            href={href}
            target={name === "Email" ? undefined : "_blank"}
            rel="noopener noreferrer"
            aria-label={`Partager sur ${name}`}
            title={`Partager sur ${name}`}
            data-testid={testId}
            className="group flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-[#BFBFBF]/60 text-[#4A4A4A] hover:border-[#C46A4A]/60 hover:text-[#C46A4A] hover:-translate-y-0.5 transition-all duration-300 text-xs font-body font-medium"
          >
            <Icon size={15} strokeWidth={1.8} />
            <span className="hidden sm:inline">{name}</span>
          </a>
        ))}
        <button
          onClick={handleCopy}
          aria-label="Copier le lien"
          title="Copier le lien"
          data-testid="share-copy"
          className={`group flex items-center gap-2 px-4 py-2.5 rounded-full border transition-all duration-300 text-xs font-body font-medium hover:-translate-y-0.5 ${
            copied
              ? "bg-[#2F4F4F] border-[#2F4F4F] text-white"
              : "bg-white border-[#BFBFBF]/60 text-[#4A4A4A] hover:border-[#C46A4A]/60 hover:text-[#C46A4A]"
          }`}
        >
          {copied ? <Check size={15} strokeWidth={2.2} /> : <LinkIcon size={15} strokeWidth={1.8} />}
          <span className="hidden sm:inline">{copied ? "Copié !" : "Copier le lien"}</span>
        </button>
      </div>
    </div>
  );
}
